import { useEffect, useRef, useState } from 'react';

/**
 * Animate a number toward its new value instead of snapping to it.
 *
 * Used for the totals that change at the end of a round: watching 8 climb to 20
 * tells you how big the swing was, where a jump just shows the result.
 *
 * Honours the reduced-motion setting, in which case the value is set directly.
 */
export function useCountUp(value: number, durationMs = 600): number {
  const [shown, setShown] = useState(value);
  const fromRef = useRef(value);

  useEffect(() => {
    const from = fromRef.current;
    if (from === value) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      fromRef.current = value;
      setShown(value);
      return;
    }

    let frame = 0;
    const started = performance.now();

    const step = (now: number) => {
      const progress = Math.min((now - started) / durationMs, 1);
      // Ease out, so it settles rather than stopping dead.
      const eased = 1 - (1 - progress) ** 3;
      setShown(Math.round(from + (value - from) * eased));

      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        fromRef.current = value;
      }
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, durationMs]);

  return shown;
}
